import { useRef, useMemo } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";

export default function SkyWithCloud() {
  const extraRef = useRef();
  const specialRef = useRef();
  const farRef = useRef(); // new ref for far clouds

  // Load textures
  const [introCloud, tintCloud] = useLoader(THREE.TextureLoader, [
    "/images/intro-cloud.png",
    "/images/Cloud-tint-8.png",
  ]);

  const introAspect = 1237 / 481;
  const tintAspect = 981 / 339;

  const materials = useMemo(
    () => [
      new THREE.SpriteMaterial({
        map: introCloud,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      }),
      new THREE.SpriteMaterial({
        map: tintCloud,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      }),
    ],
    [introCloud, tintCloud]
  );

  // Random clouds
  const randomClouds = useMemo(() => {
    return Array(550)
      .fill(0)
      .map(() => {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * 26;
        const x = Math.cos(angle) * radius * 1.5;
        const z = Math.sin(angle) * radius;
        const y = -1.3;
        const matIndex = Math.random() > 0.5 ? 0 : 1;
        const aspect = matIndex === 0 ? introAspect : tintAspect;
        const baseHeight = 1.4 + Math.random() * 0.8;
        const baseWidth = baseHeight * aspect;
        return {
          scale: [baseWidth, baseHeight, 0.1],
          position: [x, y, z],
          rotationY: Math.random() * Math.PI * 2,
          material: matIndex,
        };
      });
  }, []);

  const modelsData = [
    [-18, -1.3, 3],
    [0, -1.3, 0],
    [-6, -1.3, 0.3],
    [-2.8, -1.3, -1],
    [-11, -1.3, 0.8],
    [2.5, -1.3, -1.3],
    [7, -1.3, 0.3],
    [12, -1.3, 0.8],
    [20, -1.3, 2],
  ];

  const buildingClouds = useMemo(() => {
    return modelsData.flatMap((pos) =>
      Array(12)
        .fill(0)
        .map(() => {
          const offsetX = (Math.random() - 0.5) * 3;
          const offsetZ = Math.random() * 10;
          const y = -1.3;
          const matIndex = Math.random() > 0.5 ? 0 : 1;
          const aspect = matIndex === 0 ? introAspect : tintAspect;
          const baseHeight = 0.6 + Math.random() * 0.4;
          const baseWidth = baseHeight * aspect;
          return {
            scale: [baseWidth, baseHeight, 0.1],
            position: [pos[0] + offsetX, y, pos[2] + offsetZ],
            rotationY: Math.random() * Math.PI * 2,
            material: matIndex,
          };
        })
    );
  }, []);

  const farCloudPositions = [
    { x: -1, z: 50 },
    // { x: 1, z: 48 },
    { x: -8, z: 46 },
    { x: 12, z: 44 },
    { x: -18, z: 42 },
    // { x: 0, z: 40 },
    { x: 10, z: 38 },
    { x: -5, z: 36 },
    { x: 7, z: 34 },
    // { x: -12, z: 32 },
    { x: 3, z: 30 },
    { x: 0, z: 28 },
    { x: -7, z: 26 },
    { x: 15, z: 24 },
    { x: -10, z: 22 },
    { x: 5, z: 20 },
    { x: -3, z: 18 },
    { x: 8, z: 16 },
    { x: -6, z: 14 },
    { x: 0, z: 12 },
  ];

  const farClouds = useMemo(() => {
    return farCloudPositions.map((pos) => {
      const y = -2;
      const matIndex = Math.random() > 0.5 ? 0 : 1;
      const aspect = matIndex === 0 ? introAspect : tintAspect;
      const baseHeight = 4.0 + Math.random() * 2.0;
      const baseWidth = baseHeight * aspect;
      return {
        scale: [baseWidth, baseHeight, 0.1],
        position: [pos.x, y, pos.z],
        rotationY: Math.random() * Math.PI * 2,
        material: matIndex,
      };
    });
  }, []);

  const externalClouds = [
    { position: [0, -1.3, 6], scale: [3, 1.5, 0.1] },
    { position: [5, -1.3, 7], scale: [3.5, 1.8, 0.1] },
    { position: [-5, -1.3, 5], scale: [3, 1.5, 0.1] },
    { position: [12, -1.3, 4], scale: [3.5, 1.7, 0.1] },
    { position: [-12, -1.3, 6], scale: [3, 1.5, 0.1] },
    { position: [8, -1.3, 2], scale: [3.5, 1.7, 0.1] },
    { position: [-8, -1.3, 3], scale: [3, 1.5, 0.1] },
    { position: [15, -1.3, 1], scale: [4, 2, 0.1] },
    { position: [-15, -1.3, 2], scale: [4, 2, 0.1] },
    { position: [0, -1.3, 0], scale: [3.5, 1.7, 0.1] },
  ];

  // Animate subtle drift
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();

    if (extraRef.current) {
      extraRef.current.children.forEach((child, i) => {
        child.position.x += Math.sin(t * 0.01 + i) * 0.001;
      });
    }

    if (specialRef.current) {
      specialRef.current.children.forEach((child, i) => {
        child.position.z += Math.cos(t * 0.01 + i) * 0.001;
      });
    }

    // gentle drifting for far clouds (x-axis only)
    if (farRef.current) {
      farRef.current.children.forEach((child, i) => {
        child.position.x += Math.sin(t * 0.02 + i * 0.3) * 0.0005; // slow sideways drift
      });
    }
  });

  return (
    <>
      {/* Random clouds */}
      <group ref={extraRef}>
        {randomClouds.map((cloud, i) => (
          <sprite
            key={`rand-${i}`}
            material={materials[cloud.material]}
            scale={cloud.scale}
            position={cloud.position}
            rotation={[0, cloud.rotationY, 0]}
            frustumCulled={false}
          />
        ))}
      </group>

      {/* Building clouds */}
      <group>
        {buildingClouds.map((cloud, i) => (
          <sprite
            key={`bld-${i}`}
            material={materials[cloud.material]}
            scale={cloud.scale}
            position={cloud.position}
            rotation={[0, cloud.rotationY, 0]}
            frustumCulled={false}
          />
        ))}
      </group>

      {/* External clouds */}
      <group ref={specialRef}>
        {externalClouds.map((cloud, i) => (
          <sprite
            key={`ext-${i}`}
            material={materials[1]}
            scale={cloud.scale}
            position={cloud.position}
            frustumCulled={false}
          />
        ))}
      </group>

      {/* Far clouds (now animated) */}
      <group ref={farRef}>
        {farClouds.map((cloud, i) => (
          <sprite
            key={`far-${i}`}
            material={materials[cloud.material]}
            scale={cloud.scale}
            position={cloud.position}
            rotation={[0, cloud.rotationY, 0]}
            frustumCulled={false}
          />
        ))}
      </group>
    </>
  );
}
